/**
 * HandRule controller — deterministic heuristic baseline for the 2D rocket lander.
 *
 * Control strategy:
 *   1. Horizontal guidance: if displaced from target, generate a desired pitch
 *      to steer back. Desired pitch magnitude grows with horizontal error and
 *      is damped by horizontal velocity.
 *   2. Attitude stabilization: gimbal commands reduce pitch error toward the
 *      desired pitch, respecting the corrected sign convention
 *      (tau_y = -d * T * sin(gimbal), so gimbal output is negated).
 *   3. Vertical descent: throttle tracks a target vertical velocity profile.
 *      Target velocity is negative during descent, flaring to near zero at
 *      touchdown. Throttle includes gravity-compensation feedforward.
 *   4. Fuel awareness: throttle is reduced when fuel is low to preserve
 *      margin for landing.
 *
 * All outputs are clamped to physical limits. No randomness is used.
 */

import { BaseController } from "./base";
import type { Environment, RocketParams, RocketState, ControlAction } from "../types";

export interface HandRuleConfig {
  /** Horizontal position error gain for desired pitch (1/m). */
  readonly kpX: number;
  /** Horizontal velocity damping gain for desired pitch (s/m). */
  readonly kdX: number;
  /** Maximum desired pitch angle (rad). */
  readonly maxPitch: number;
  /** Pitch proportional gain for gimbal (1/rad). */
  readonly kpTheta: number;
  /** Pitch rate damping gain for gimbal (s/rad). */
  readonly kdTheta: number;
  /** Vertical velocity proportional gain for throttle (s/m). */
  readonly kpZ: number;
  /** Vertical velocity integral gain for throttle (1/m). */
  readonly kiZ: number;
  /** Descent rate magnitude before flare (m/s). */
  readonly descentRate: number;
  /** Altitude where flare to zero vertical velocity begins (m). */
  readonly flareAltitude: number;
}

export const DefaultHandRuleConfig: HandRuleConfig = {
  kpX: 0.003,
  kdX: 0.08,
  maxPitch: 0.25,
  kpTheta: 1.5,
  kdTheta: 0.3,
  kpZ: 0.18,
  kiZ: 0.02,
  descentRate: 10,
  flareAltitude: 100,
};

export class HandRuleController extends BaseController {
  readonly name = "hand-rule";

  private config: HandRuleConfig;
  private rocket: RocketParams;
  private integralVz = 0;

  constructor(rocket: RocketParams, config: Partial<HandRuleConfig> = {}) {
    super();
    this.rocket = rocket;
    this.config = { ...DefaultHandRuleConfig, ...config };
  }

  reset(): void {
    this.integralVz = 0;
  }

  compute(state: RocketState, env: Environment, dt: number): ControlAction {
    const {
      kpX,
      kdX,
      maxPitch,
      kpTheta,
      kdTheta,
      kpZ,
      kiZ,
      descentRate,
      flareAltitude,
    } = this.config;
    const { dryMass, maxThrust } = this.rocket;

    // --- Layer 1: Guidance — horizontal position/velocity → desired pitch ---
    const xError = -state.x; // target is at x=0; positive error means right of target
    // If x > 0, steer nose toward -X (negative theta) to move back toward target.
    let thetaCmd = kpX * xError - kdX * state.vx;
    thetaCmd = Math.min(maxPitch, Math.max(-maxPitch, thetaCmd));

    // --- Layer 2: Attitude — desired pitch vs current → gimbal ---
    // e_theta = theta_cmd - theta
    const eTheta = thetaCmd - state.theta;
    // de_theta/dt ≈ -omega (theta_cmd is constant within a step)
    const deThetaDt = -state.omega;
    // PI/D on attitude error, then NEGATE because tau_y = -d * T * sin(gimbal)
    let gimbalCmd = -(kpTheta * eTheta + kdTheta * deThetaDt);
    gimbalCmd = Math.min(0.4, Math.max(-0.4, gimbalCmd));

    // --- Layer 3: Vertical — vertical velocity error → throttle ---
    // Altitude-dependent descent velocity profile.
    let vzTarget: number;
    if (state.z > flareAltitude) {
      vzTarget = -descentRate;
    } else if (state.z > 0) {
      vzTarget = -descentRate * (state.z / flareAltitude);
    } else {
      vzTarget = 0;
    }

    const eVz = vzTarget - state.vz;
    // Anti-windup: only integrate when not saturated.
    this.integralVz += eVz * dt;
    const integralMax = 500; // clamp integral to prevent windup
    this.integralVz = Math.min(integralMax, Math.max(-integralMax, this.integralVz));

    // Desired vertical acceleration.
    const aDes = kpZ * eVz + kiZ * this.integralVz;

    // Required total vertical force: gravity + acceleration.
    const mass = dryMass + state.fuel;
    const fzReq = mass * (env.gravity + aDes);

    // Account for vertical component of thrust vector.
    const thetaPlusGimbal = state.theta + gimbalCmd;
    const cosTerm = Math.cos(thetaPlusGimbal);
    const safeCos = Math.abs(cosTerm) < 0.1 ? (cosTerm < 0 ? -0.1 : 0.1) : cosTerm;

    let throttleCmd = fzReq / (maxThrust * safeCos);
    throttleCmd = Math.min(1, Math.max(0, throttleCmd));

    // Fuel awareness: reduce throttle when fuel is low to preserve landing margin.
    if (state.fuel < 500) {
      throttleCmd *= state.fuel / 500;
    }

    return { throttle: throttleCmd, gimbal: gimbalCmd };
  }
}
